import type { ActivityOf } from '@wyn/content';
import { createRng } from '../rng.ts';
import { BTN, DT, type RealtimeSim, TICK_HZ } from './realtime.ts';
import { clamp, type Fx, type MinigameResult } from './types.ts';

/** Canvas units: the pointer arrives in these. */
export const ARCHERY_W = 340;
export const ARCHERY_H = 300;
/** The target's centre. */
export const ARCHERY_CENTRE = { x: 170, y: 140 } as const;
const PAUSE = 0.7;
const MAX_SECONDS = 180;

export interface ArcheryHit {
  x: number;
  y: number;
  ring: number;
}

/** The pointer is the aim; hold BTN.main to draw, let go to loose. */
export interface ArcherySim extends RealtimeSim {
  /** Where the player aims (the pointer). */
  aim: { x: number; y: number };
  /** Where the sight actually sits after sway. */
  sight: { x: number; y: number };
  /** Draw, 0–1. */
  power: number;
  /** >1 once the arm tires at full draw. */
  tire: number;
  drawing: boolean;
  wind: number;
  shot: number;
  arrows: number;
  total: number;
  hits: ArcheryHit[];
  /** Target radius in canvas units (smaller at range). */
  radius: number;
  /** How far the wind carries an arrow, in canvas units per unit of wind. */
  drift: number;
}

/**
 * The Butts. Aim with the pointer against sway and wind, hold to draw and release to loose.
 * Hold full draw too long and the arm tires. Ported from the Harrowmere prototype.
 */
export function archerySim(a: ActivityOf<'archery'>, level: number, seed: string): ArcherySim {
  const rng = createRng(seed);
  const rand = (lo: number, hi: number) => lo + rng.next() * (hi - lo);
  const { x: cx, y: cy } = ARCHERY_CENTRE;
  const amp0 = clamp(14 - level * 0.3, 6, 14) * a.distance;
  let held = 0, pause = 0, was = false, t = 0;

  const fx = (text: string, tone: Fx['tone'], cue?: string, power?: number) => sim.fx.push({ tick: sim.tick, text, tone, cue, power });
  const end = (r: MinigameResult) => void (sim.result = r);
  const finish = () => {
    const score = sim.total / (a.arrows * 10);
    const coin = Math.round(a.prize * clamp((score - 0.3) / 0.6, 0, 1));
    end({
      score,
      title: `${sim.total} points`,
      text: coin ? `The marshal awards you ${coin} coin.` : 'You leave the field without a purse.',
      breakdown: [['Accuracy', score]],
      yield: null,
      coin,
      xpMult: 1,
      feats: sim.total >= a.arrows * 10 ? ['perfect-card'] : [],
    });
  };

  const sim: ArcherySim = {
    tick: 0,
    result: null,
    fx: [],
    aim: { x: cx, y: cy },
    sight: { x: cx, y: cy },
    power: 0,
    tire: 1,
    drawing: false,
    wind: rand(-a.wind, a.wind),
    shot: 0,
    arrows: a.arrows,
    total: 0,
    hits: [],
    radius: 110 / a.distance,
    drift: 12 * a.distance,
    step(mask, x, y) {
      if (sim.result) return;
      sim.tick++;
      t += DT;
      pause -= DT;
      sim.aim = { x: clamp(x, 0, ARCHERY_W), y: clamp(y, 0, ARCHERY_H) };
      const on = (mask & BTN.main) !== 0 && pause <= 0;
      if (on) {
        sim.power = Math.min(1, sim.power + DT / 0.8);
        held += DT;
      }
      sim.tire = held > 2.3 ? 1 + (held - 2.3) * 0.9 : 1;
      const amp = amp0 * sim.tire * (on ? 1 : 1.3);
      sim.sight = {
        x: sim.aim.x + Math.sin(t * 1.3) * amp + Math.sin(t * 3.1) * amp * 0.3,
        y: sim.aim.y + Math.cos(t * 1.7) * amp * 0.8,
      };
      sim.drawing = on;
      if (!on && was && sim.power > 0.05) {
        const lx = sim.sight.x + sim.wind * sim.drift + rand(-2, 2);
        const ly = sim.sight.y + (1 - sim.power) * 90 + rand(-2, 2);
        const d = (Math.hypot(lx - cx, ly - cy) / sim.radius) * 10;
        const ring = d < 10 ? 10 - Math.floor(d) : 0;
        sim.hits.push({ x: lx, y: ly, ring });
        sim.total += ring;
        sim.shot++;
        fx(ring >= 9 ? `${ring}! Bullseye` : ring ? `${ring}` : 'Miss', ring >= 9 ? 'gold' : ring >= 5 ? 'good' : 'bad', 'hit', ring / 10);
        sim.power = 0;
        held = 0;
        pause = PAUSE;
        if (sim.shot >= a.arrows) return finish();
        sim.wind = rand(-a.wind, a.wind);
      }
      was = on;
      // Arrows not loosed in time count as misses.
      if (sim.tick >= MAX_SECONDS * TICK_HZ) finish();
    },
  };
  return sim;
}
